import { isEmpty } from 'fast-glob/out/utils/string';
import { Language } from 'utils/i18n/Language';

class FormattingService {
  static readonly EMPTY = '';
  private locale: string | string[];

  constructor() {
    this.locale = Language.DEFAULT.locale;
  }

  getLocale(): string | string[] {
    return this.locale;
  }

  setLocale(locale: string | string[]): this {
    this.locale = locale;
    return this;
  }

  formatDateShort(value: Date): string {
    return this.formatDate(value, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  formatDateNumeric(value: Date): string {
    return this.formatDate(value, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
  }

  formatDateLong(value: Date): string {
    return this.formatDate(value, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  formatTime(value: Date): string {
    return this.formatDate(value, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
  }

  formatDateLongNoYear(value: Date): string {
    return this.formatDate(value, {
      month: 'long',
      day: 'numeric',
    });
  }

  formatDateMonth(value: Date): string {
    return this.formatDate(value, {
      year: 'numeric',
      month: 'numeric',
    });
  }

  formatDateHourMinute(value: Date): string {
    return this.formatDate(value, {
      hour: 'numeric',
      minute: 'numeric',
    });
  }

  formatLocaleTimeString(value: Date): string {
    return this.formatDate(value, {
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short',
    });
  }

  formatDateRelative(value: number, unit: 'hour' | 'day'): string {
    if (!value) {
      return FormattingService.EMPTY;
    }
    return new Intl.RelativeTimeFormat('en', {
      localeMatcher: 'best fit', // other values: "lookup"
      numeric: 'always', // other values: "auto"
      style: 'long', // other values: "short" or "narrow"
    }).format(value, unit);
  }

  formatDate(value: Date, options?: Intl.DateTimeFormatOptions): string {
    if (!value) {
      return FormattingService.EMPTY;
    }
    return new Intl.DateTimeFormat(this.locale, options).format(value);
  }

  formatNumber(value: number, maximumFractionDigits = 2, minimumFractionDigits = 0): string {
    if (isNaN(value)) {
      return FormattingService.EMPTY;
    }
    const number = new Intl.NumberFormat(this.locale, {
      style: 'decimal',
      currencyDisplay: 'symbol', // localized symbol
      maximumFractionDigits,
      minimumFractionDigits,
    }).format(value);
    return replaceNoBreakSpace(number);
  }

  getThousandSeparator(): string {
    return Intl.NumberFormat(this.locale)
      .format(11111)
      .replace(/\p{Number}/gu, '');
  }

  getDecimalSeparator(): string {
    return Intl.NumberFormat(this.locale)
      .format(1.1)
      .replace(/\p{Number}/gu, '');
  }

  parseLocaleNumber(stringNumber: string): number {
    if (!stringNumber) {
      return NaN;
    }

    const possibleSeparators = [',', '.'];
    let decimalSeparatorDetected = null;
    for (const sep of possibleSeparators) {
      const regex = new RegExp(`\\${sep}\\d{1,2}$`);
      if (regex.test(stringNumber)) {
        decimalSeparatorDetected = sep;
        break;
      }
    }

    if (!decimalSeparatorDetected) {
      decimalSeparatorDetected = this.getDecimalSeparator();
    }

    const thousandSeparator = this.getThousandSeparator();
    let normalized = stringNumber.replace(/\s+/g, '');
    if (thousandSeparator !== decimalSeparatorDetected) {
      const tsRegex = new RegExp(`\\${thousandSeparator}`, 'g');
      normalized = normalized.replace(tsRegex, '');
    }

    if (decimalSeparatorDetected !== '.') {
      const dsRegex = new RegExp(`\\${decimalSeparatorDetected}`, 'g');
      normalized = normalized.replace(dsRegex, '.');
    }

    normalized = normalized.replace(/[^\d.-]/g, '');
    return parseFloat(normalized);
  }

  formatCurrency(value: number | string, currency: string | undefined): string {
    if (Number.isNaN(value) || !currency || isEmpty(currency)) {
      return FormattingService.EMPTY;
    }
    const number = Intl.NumberFormat(this.locale, {
      style: 'currency',
      currencyDisplay: 'symbol',
      useGrouping: true,
      currency,
    }).format(Number(value));
    return replaceNoBreakSpace(number);
  }

  formatPercent(value: number | undefined): string {
    if (value === undefined) {
      return FormattingService.EMPTY;
    }
    const formatted = Intl.NumberFormat(this.locale, {
      style: 'percent',
      maximumFractionDigits: 2,
    }).format(value);
    return replaceNoBreakSpace(formatted);
  }

  formatPercentDecimal(value: number | undefined): string {
    if (value === undefined) {
      return FormattingService.EMPTY;
    }
    return this.formatPercent(value / 100);
  }

  formatAmount(value: number, unit: 'hour' | 'day', display: 'short' | 'long' = 'short'): string {
    if (!value) {
      return FormattingService.EMPTY;
    }
    const number = Intl.NumberFormat(this.locale, {
      style: 'unit',
      unitDisplay: display,
      unit,
    }).format(value);
    return replaceNoBreakSpace(number);
  }

  getCurrencySymbol(currency: string): string {
    return new Intl.NumberFormat(this.locale, {
      currency,
      style: 'currency',
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .format(1)
      .replace(/\d/g, '')
      .trim();
  }

  getCurrencyPlacement(currency: string): 'before' | 'after' {
    const formattedCurrency = new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      currency,
    }).format(1);
    const currencySymbol = this.getCurrencySymbol(currency);
    return formattedCurrency.startsWith(currencySymbol) ? 'before' : 'after';
  }

  formatTextOverflow({
    src,
    maxWordLength = 25,
    maxLength = 100,
  }: {
    src?: string;
    maxWordLength?: number;
    maxLength?: number;
  }): string {
    if (src === undefined) {
      return '';
    }
    const shorten = src
      .split(' ')
      .map((word) => (word.length > maxWordLength ? `${word.substring(0, maxWordLength - 3)}...` : word))
      .join(' ');
    return shorten.length > maxLength ? shorten.substring(0, maxLength) + '...' : shorten;
  }
}

export const isValidDate = (date: string): boolean => {
  const dateTime = new Date(date);
  return !isNaN(dateTime.getTime());
};

function replaceNoBreakSpace(str: string): string {
  // Renault font do not support 'NARROW NO-BREAK SPACE'
  return str.replaceAll(String.fromCharCode(parseInt('202f', 16)), String.fromCharCode(parseInt('00A0', 16)));
}

export default FormattingService;
